import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faGlobeAmericas } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'
import { AVAILABLE_LANGUAGES, LocalesType, getLocaleDisplayConfig } from '@commutifi-fe/constants'
import React from 'react'
import { useLocaleReceiver } from '../../locales/LocaleReceiver'
import { colors, spacing } from '../../style/constants'
import { Dropdown } from '../../Dropdown'
import { Flex } from '../../Flex'
import { Typography } from '../../AppTypography'
import { Menu } from '../../Menu'
import './styles.scss'

export interface CommutifiFooterProps {
  children?: React.ReactNode
  languageSwitcher?: React.ReactNode
}

interface FooterItemProps {
  children: React.ReactNode
  className?: string
}

function FooterItem({ children, className }: FooterItemProps) {
  return <div className={cx('app-footer-item', className)}>{children}</div>
}

export function CommutifiFooter({ children, languageSwitcher }: CommutifiFooterProps) {
  const intl = useLocaleReceiver('Footer')
  return (
    <Menu
      className="app-commutifi-footer"
      mode="horizontal"
      // We just was a responsive footer, not really a menu
      selectedKeys={[]}
      items={[
        {
          key: 'children',
          label: children
        },
        React.Children.count(languageSwitcher) > 0
          ? {
              key: 'footer-language-switcher',
              label: <FooterItem className="app-language-switcher">{languageSwitcher}</FooterItem>
            }
          : null,
        {
          key: 'footer-terms',
          label: (
            <FooterItem>
              <a href="http://www.commutifi.com/terms-of-use" target="_blank" rel="noreferrer">
                <span>{intl.formatMessage({ id: 'termsLabel' })}</span>
              </a>
            </FooterItem>
          )
        },
        {
          key: 'footer-privacy-policy',
          label: (
            <FooterItem>
              <a href="http://www.commutifi.com/privacy-policy" target="_blank" rel="noreferrer">
                <span>{intl.formatMessage({ id: 'privacyLabel' })}</span>
              </a>
            </FooterItem>
          )
        },
        {
          key: 'footer-website',
          label: (
            <FooterItem>
              <a href="http://www.commutifi.com" target="_blank" rel="noreferrer">
                {intl.formatMessage({ id: 'learnAboutCommutifiLabel' })}
              </a>
            </FooterItem>
          )
        }
      ].filter((i) => i && i.label)}
    />
  )
}

const renderCurrentLanguage = (currentLanguageLabel: React.ReactNode) => (
  <div className="app-language-switcher-button">
    <span>{currentLanguageLabel}</span>
    <FontAwesomeIcon icon={faCaretDown} className="u-margin-left--small" />
  </div>
)

export interface OfflineLocaleSelectorProps {
  invertColor?: boolean
  renderCurrentLanguage?: (currentOption: React.ReactNode) => React.ReactNode
  locale: LocalesType
  onChange?: (localeId: string) => void
  availableLocales?: (typeof AVAILABLE_LANGUAGES)[number]['id'][]
}

/**
 * Switches locales and saves it in the local storage using persisted Redux state.
 * This is useful to display locale selector in an 'offline' context (user is not logged in)
 */
export function OfflineLocaleSelector({
  invertColor = false,
  renderCurrentLanguage,
  onChange,
  locale,
  availableLocales
}: OfflineLocaleSelectorProps) {
  const currentLocaleConfig = React.useMemo(() => getLocaleDisplayConfig(locale), [locale])
  const locales = React.useMemo(
    () =>
      availableLocales ? AVAILABLE_LANGUAGES.filter(({ id }) => availableLocales.includes(id)) : AVAILABLE_LANGUAGES,
    [availableLocales]
  )

  return (
    <Dropdown
      // Trigger click option is there to support keyboard navigation and mobile devices
      trigger={['click', 'hover']}
      menu={{
        // @ts-expect-error -- It does ...
        'data-testid': 'locale-selector',
        items: locales
          .filter((l) => l.id !== locale)
          .map(({ id, label, icon: Icon }) => ({
            key: id,
            label: (
              <Flex align="center">
                <Icon
                  title={label}
                  style={{
                    borderRadius: '50%',
                    height: '20px',
                    width: '20px',
                    border: `1px solid ${colors.neutral300}`,
                    marginRight: spacing.small
                  }}
                />
                {label}
              </Flex>
            ),
            onClick: () => onChange?.(id)
          }))
      }}
    >
      {renderCurrentLanguage ? (
        <Typography.Text size="small" block ellipsis tabIndex={0} style={{ marginBottom: 0, color: colors.neutral500 }}>
          {renderCurrentLanguage(currentLocaleConfig?.label)}
        </Typography.Text>
      ) : (
        <Typography.Text size="small" block ellipsis className={cx(invertColor && 'u-color--white')} tabIndex={0}>
          <FontAwesomeIcon icon={faGlobeAmericas} size="lg" />
          <span className="u-margin-left--small">{currentLocaleConfig?.label}</span>
          <FontAwesomeIcon icon={faCaretDown} className="u-margin-left--tiny" />
        </Typography.Text>
      )}
    </Dropdown>
  )
}

function FooterLocaleSelector(props: OfflineLocaleSelectorProps) {
  return <OfflineLocaleSelector {...props} renderCurrentLanguage={renderCurrentLanguage} />
}
CommutifiFooter.OfflineLocaleSelector = FooterLocaleSelector

export default CommutifiFooter
